import React, { FC, useEffect, useRef, useState } from "react";
import styled, { css } from "styled-components";
import { BaseLink } from "../BaseLink";
import { ConditionalWrapper } from "../ConditionalWrapper";
import { Tooltip, TooltipChildren } from "../Tooltip";
import { ProductIconType } from "../constants/productIcon";
import { style } from "../style";
import { ResourceIcon } from "./ResourceIcon";

export enum ResourceItemSize {
  DEFAULT = "default",
  COMPACT = "compact",
}

export const ResourceItemMeta = styled.div<{ $isCompact: boolean }>`
  display: flex;
  flex-direction: column;
  overflow: hidden;
  flex: 1;
  margin-left: 12px;
`;

export const ResourceItemName = styled.div<{ $isLink: boolean }>`
  color: ${({ $isLink }) => ($isLink ? style.colors.primary.base : style.colors.grey.darkest)};
  font-weight: ${style.vars.fontWeight.bold};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const ResourceItemDescription = styled.div`
  font-size: 0.8em;
  color: ${style.colors.grey.dark};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Wrapper = styled.div<{
  $isCompact: boolean;
  $showDescriptionOnHover: boolean;
}>`
  display: flex;
  flex-direction: row;
  align-items: center;
  height: ${({ $isCompact }) => ($isCompact ? "40px" : "50px")};
  min-width: 0;

  /* Ensures that the name gets properly truncated in case Meta is wrapped
  in the ConditionalWrapper */
  div:nth-child(2) {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  ${({ $showDescriptionOnHover }) =>
    $showDescriptionOnHover &&
    css`
      cursor: pointer;
      ${ResourceItemName} {
        transform: translate3d(0, 8px, 0);
        transition: transform 0.4s ease-in-out;
      }
      ${ResourceItemDescription} {
        opacity: 0;
        transform: translate3d(0, 8px, 0);
        transition: transform 0.4s ease-in-out, opacity 0.4s ease-in;
      }

      &:hover {
        ${ResourceItemName} {
          transform: translate3d(0, 0, 0);
        }

        ${ResourceItemDescription} {
          opacity: 1;
          transform: translate3d(0, 0, 0);
        }
      }
    `}
`;

type Props = {
  name: string;
  description?: string;
  icon: ProductIconType;
  status?: string;
  size?: ResourceItemSize;
  to?: string;
  showDescriptionOnHover?: boolean;
  forceExternal?: boolean;
};

export const ResourceItem: FC<Props> = ({
  name,
  description,
  icon,
  status,
  size = ResourceItemSize.DEFAULT,
  to,
  forceExternal = false,
  showDescriptionOnHover = false,
}) => {
  const isCompact = size === ResourceItemSize.COMPACT;
  const isLink = !!to;
  const nameId = `${name}-name`;
  const descriptionId = `${name}-description`;
  const nameRef = useRef<HTMLDivElement>();
  // eslint-disable-next-line @typescript-eslint/naming-convention
  const [showTooltip, setShowTooltip] = useState(false);

  // TODO: make this responsive
  useEffect(() => {
    if (nameRef.current) {
      const offsetWidth = nameRef.current?.offsetWidth ?? 0;
      const scrollWidth = nameRef.current?.scrollWidth ?? 0;
      if (offsetWidth < scrollWidth) {
        setShowTooltip(true);
      }
    }
  }, [nameRef.current]);

  const meta = (
    <ConditionalWrapper
      condition={showTooltip}
      wrapper={(children) => (
        <Tooltip content={name}>
          <TooltipChildren>{children}</TooltipChildren>
        </Tooltip>
      )}
    >
      <ResourceItemMeta $isCompact={isCompact}>
        <ResourceItemName
          ref={nameRef as React.RefObject<HTMLDivElement>}
          id={nameId}
          aria-describedby={description ? descriptionId : undefined}
          $isLink={isLink}
        >
          {name}
        </ResourceItemName>
        {!!description && <ResourceItemDescription id={descriptionId}>{description}</ResourceItemDescription>}
      </ResourceItemMeta>
    </ConditionalWrapper>
  );

  return (
    <ConditionalWrapper
      condition={isLink}
      wrapper={(children) => (
        <BaseLink to={to ?? ""} forceExternal={forceExternal}>
          {children}
        </BaseLink>
      )}
    >
      <Wrapper $isCompact={isCompact} $showDescriptionOnHover={!!description && showDescriptionOnHover}>
        <ResourceIcon inline={size === ResourceItemSize.COMPACT} icon={icon} status={status} />
        {meta}
      </Wrapper>
    </ConditionalWrapper>
  );
};
